import InfoRounded from '@mui/icons-material/InfoRounded';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/system';
import { Checkbox } from '@openx/components-core/lib/Checkbox/Checkbox.tsx';
import { RHFCheckboxField } from '@openx/components-core/lib/CheckboxField/RHFCheckboxField.tsx';
import { IQ_SCORE_VAL, TargetingFields } from '@openx/types/targeting/targeting.ts';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

type ExcludeBelowAverageDomainsProps = {
  disabled?: boolean;
  readonly?: boolean;
  value?: boolean;
  error?: boolean;
  helperText?: string;
  showTooltip?: boolean;
};

export const ExcludeBelowAverageDomains = ({
  disabled,
  readonly,
  value,
  error,
  helperText,
  showTooltip = true,
}: ExcludeBelowAverageDomainsProps) => {
  const { t } = useTranslation();

  const inventoryQualityCheckedValue = React.useMemo(
    () => ({
      iq_score: {
        op: '>=',
        val: IQ_SCORE_VAL,
      },
    }),
    []
  );

  const isInventoryQualityChecked = React.useCallback((v: unknown) => {
    if (!v || typeof v !== 'object') return false;
    return typeof (v as any).iq_score === 'object' && (v as any).iq_score !== null;
  }, []);

  return (
    <Grid
      size={12}
      sx={{
        display: 'block',
        mb: 1.5,
        pl: readonly ? 0.4 : 0.5,
      }}
    >
      <Stack
        spacing={1}
        direction="row"
        sx={{
          alignItems: 'center',
        }}
      >
        {readonly ? (
          <FormControlLabel
            control={<Checkbox checked={!!value} disabled />}
            label={t('Exclude below-average quality domains')}
            data-test="exclude-below-average-quality-domains"
            sx={{
              pt: 1,
              '& .MuiCheckbox-root': {
                ml: '-8px',
              },
            }}
          />
        ) : (
          <RHFCheckboxField
            name={`targeting.${TargetingFields.INVENTORY_QUALITY}`}
            label={t('Exclude below-average quality domains')}
            data-test="exclude-below-average-quality-domains"
            disabled={disabled}
            error={error}
            helperText={helperText}
            valueOfChecked={inventoryQualityCheckedValue}
            valueOfUnchecked={null}
            isChecked={isInventoryQualityChecked}
          />
        )}
        {showTooltip && (
          <Tooltip
            placement="right"
            slotProps={{
              tooltip: { sx: { maxWidth: 560, p: 2 } },
            }}
            title={
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <Trans i18nKey="iq.tooltip.worksOnly" components={{ strong: <strong /> }}>
                    Applies only for <strong>web domains</strong>; does not apply to in-app or CTV inventory.
                  </Trans>
                </Typography>

                <Typography variant="body2" sx={{ mt: 2, mb: 0.5 }}>
                  {t(
                    'Domains flagged as below-average quality may have high ad clutter, slow page load, limited addressability, low domain authority, abnormal traffic patterns, and other factors that negatively impact user experience and advertising performance.'
                  )}
                </Typography>
              </Box>
            }
          >
            <IconButton size="small" aria-label={t('Inventory Quality Scoring info')} sx={{ ml: 0.5 }}>
              <InfoRounded fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Grid>
  );
};
